import { motion } from "framer-motion";
import { Download, BarChart3 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { API_BASE } from "../lib/api";

const Transparency = () => {
  const { t } = useLanguage();
  const reportYear = new Date().getFullYear() - 1;
  const reportUrl = `${API_BASE}/annual-report?year=${reportYear}`;

  return (
    <section
      id="transparency"
      className="py-24 md:py-32 bg-[#F3F0EA]"
      data-testid="transparency-section"
    >
      <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="eyebrow" data-testid="transparency-eyebrow">{t.transparency.eyebrow}</span>
          <span className="gold-line mt-4 mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F] leading-[1.1] font-medium" data-testid="transparency-title">
            {t.transparency.title}
          </h2>
          <p className="text-[#334155] text-base md:text-lg leading-relaxed mt-6" data-testid="transparency-intro">
            {t.transparency.intro}
          </p>
          <a
            href={reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-8 text-[#002B49] hover:text-[#C5A059] transition-colors font-medium border-b border-[#002B49]/30 hover:border-[#C5A059] pb-1"
            data-testid="annual-report-link"
          >
            <Download size={16} />
            {t.transparency.reportLink} {reportYear}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(0,43,73,0.25)] border border-black/5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#64748B] font-medium mb-6">
              <BarChart3 size={14} />
              <span>{`Répartition / Allocation — 2024`}</span>
            </div>

            <div className="space-y-6">
              {t.transparency.breakdown.map((b, i) => (
                <div key={b.label} data-testid={`breakdown-${i}`}>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="font-medium text-[#0A192F]">{b.label}</span>
                    <span className="font-serif text-2xl text-[#002B49] font-semibold">
                      {b.value}%
                    </span>
                  </div>
                  <div className="h-2 bg-[#F3F0EA] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${b.value}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, delay: 0.2 + i * 0.1, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: b.color }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-[#E5E7EB] grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[#64748B] text-xs uppercase tracking-wider mb-1">
                  Audit
                </div>
                <div className="text-[#0A192F] font-medium">KPMG France</div>
              </div>
              <div>
                <div className="text-[#64748B] text-xs uppercase tracking-wider mb-1">
                  Reconnaissance
                </div>
                <div className="text-[#0A192F] font-medium">Utilité publique</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Transparency;
